import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  inject,
} from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { LeftMenuComponent } from '../left-menu/left-menu.component';
import { FooterComponent } from '../footer/footer.component';
import { InfoPopupComponent } from '../info-popup/info-popup.component';
import { UploadProgressComponent } from '../upload-progress/upload-progress.component';
import { UpdateLeadsPopupComponent } from '../../../pages/update-leads-popup/components/update-leads-popup/update-leads-popup.component';
import { PopupComponent } from '../popup/popup.component';
import { LeadsService } from '../../../pages/leads/services/leads.service';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { takeUntil, filter, forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { CommonService } from '../../services/common/common.service';

@Component({
  selector: 'app-authorized-layout',
  standalone: true,
  imports: [
    HeaderComponent,
    LeftMenuComponent,
    FooterComponent,
    InfoPopupComponent,
    UploadProgressComponent,
    UpdateLeadsPopupComponent,
    PopupComponent,
  ],
  templateUrl: './authorized-layout.component.html',
  styleUrl: './authorized-layout.component.scss',
})
export class AuthorizedLayoutComponent extends DestroyBase implements OnInit {
  private leadsService = inject(LeadsService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private commonService = inject(CommonService);

  isSmeUser = this.authService.isSMEUser();
  hasLeadsPermission = this.authService.hasPermission('custom.get_set_leads');
  hasLeadsCriteriaPermission = this.authService.hasPermission(
    'custom.manage_matching_criteria',
  );
  showInfoPopup = false;
  showLeadsStatusPopup = false;
  showDeeprootsV2Announcement = false;

  private readonly INFO_POPUP_DISMISSED_KEY = 'infoPopupDismissed';
  private readonly DEEPROOTS_V2_ANNOUNCEMENT_DISMISSED_KEY =
    'deeprootsV2AnnouncementDismissed';
  private readonly LEADS_STATUS_POPUP_DISMISSED_KEY =
    'leadsStatusPopupDismissedUntil';

  @ViewChild(UploadProgressComponent)
  uploadProgressComponent?: UploadProgressComponent;

  @Output() uploadProgressToggled = new EventEmitter<void>();

  ngOnInit() {
    // Check DeepRoots v2 announcement first (higher priority)
    // this.checkDeeprootsV2Announcement();
    this.checkInfoPopup();

    if (this.hasLeadsPermission) {
      this.checkLeadStatusReminder();

      // Subscribe to token refresh events to check lead status reminder
      this.authService.onTokenRefreshed$
        .pipe(
          filter((refreshed) => refreshed === true),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.checkLeadStatusReminder();
        });
    }
  }

  private checkLeadStatusReminder() {
    // First check if the popup was dismissed and if 48 hours have passed
    const dismissedUntil = localStorage.getItem(
      this.LEADS_STATUS_POPUP_DISMISSED_KEY,
    );
    const now = new Date();

    let shouldCheck = true;

    if (dismissedUntil) {
      const dismissedDate = new Date(dismissedUntil);
      if (dismissedDate > now) {
        shouldCheck = false; // Still within 48 hour dismissal period
      }
    }

    // Only make API call if we need to check
    if (!shouldCheck) {
      return;
    }

    forkJoin([
      this.leadsService.getNoStatusLeads(1, 1),
      this.leadsService.getOutdatedStatusLeads(1, 1),
    ]).subscribe({
      next: ([noStatusRes, reminderRes]) => {
        if (
          (noStatusRes && noStatusRes.count > 0) ||
          (reminderRes && reminderRes.count > 0)
        ) {
          setTimeout(() => {
            this.showLeadsStatusPopup = true;
          }, 3000);
        }
      },
      error: (error) => {
        console.error(
          'Failed to load outdated/no-status leads for popup:',
          error,
        );
      },
    });
  }

  private checkInfoPopup() {
    const hasBeenDismissed =
      localStorage.getItem(this.INFO_POPUP_DISMISSED_KEY) === 'true';

    if (!hasBeenDismissed) {
      setTimeout(() => {
        this.showInfoPopup = true;
      }, 5000);
    }
  }

  onUploadProgressToggled(): void {
    if (this.uploadProgressComponent) {
      if (this.uploadProgressComponent.isHidden) {
        this.uploadProgressComponent.showProgress();
      } else {
        this.uploadProgressComponent.isMinimized =
          !this.uploadProgressComponent.isMinimized;
      }
    }
  }

  onInfoPopupClosed() {
    // Save the dismissed state to localStorage
    localStorage.setItem(this.INFO_POPUP_DISMISSED_KEY, 'true');
    this.showInfoPopup = false;
  }

  onLeadsStatusPopupClosed() {
    this.showLeadsStatusPopup = false;
  }

  private checkDeeprootsV2Announcement() {
    if (!this.hasLeadsCriteriaPermission) {
      return; // User doesn't have permission, so don't show announcement
    }

    const hasBeenDismissed =
      localStorage.getItem(this.DEEPROOTS_V2_ANNOUNCEMENT_DISMISSED_KEY) ===
      'true';

    if (!hasBeenDismissed) {
      // Show announcement after a short delay
      setTimeout(() => {
        this.showDeeprootsV2Announcement = true;
      }, 1000);
    }
  }

  onDeeprootsV2AnnouncementAcknowledged() {
    // Save the dismissed state to localStorage
    localStorage.setItem(this.DEEPROOTS_V2_ANNOUNCEMENT_DISMISSED_KEY, 'true');
    this.showDeeprootsV2Announcement = false;
  }

  goToLeadsCriteria() {
    this.onDeeprootsV2AnnouncementAcknowledged();
    this.router.navigate(['deeproots/leads-criteria']);
  }
}
